import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { extractYoutubeVideoId } from '../../utils/youtubeUtils';

const VideoModal = ({ open, onClose, videoUrl, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const videoId = extractYoutubeVideoId(videoUrl);
  const youtubeAppAttempted = useRef(false);

  useEffect(() => {
    if (open && isMobile && videoId && !youtubeAppAttempted.current) {
      youtubeAppAttempted.current = true;

      // Create a hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      // Try to open YouTube app
      iframe.src = `youtube://video/${videoId}`;

      // Remove the iframe after a short delay
      setTimeout(() => {
        document.body.removeChild(iframe);
        // If YouTube app didn't open, fallback to mobile web
        if (document.visibilityState !== 'hidden') {
          window.open(`https://m.youtube.com/watch?v=${videoId}`, '_blank');
        }
      }, 500);

      // Reset the attempt flag when modal closes
      return () => {
        youtubeAppAttempted.current = false;
      };
    }
  }, [open, isMobile, videoId]);

  // Don't render the dialog on mobile
  if (isMobile) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            paddingTop: '56.25%', // 16:9 Aspect Ratio
            width: '100%',
          }}
        >
          {videoId && (
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
              src={`https://www.youtube.com/embed/${videoId}?rel=0`}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VideoModal;
